import {
  BranchItemFragment,
  OnlineOrderBranchConfigurationItemFragment,
  OpeningTimeServiceType,
} from '@app/graphql/types/graphql.ts';
import Card from '@app/components/card/card.tsx';
import CardContent from '@app/components/card/card-content.tsx';

import CDNLink from '@app/utils/cdn-link.ts';
import { useTranslate } from '@tolgee/react';
import LanguageSelectorAdvanced from '@app/components/language-selector.tsx';
import { todayIs } from '@app/page/online-order/product-picker/utils/today.ts';
import { EnvelopeIcon, PhoneIcon } from '@heroicons/react/24/outline';
import SwitchModeButton from './on-off-button';
import HappyHourBanner from './happy-hour-banner';

export default function BusinessCard(props: {
  branch: BranchItemFragment;
  configuration?: OnlineOrderBranchConfigurationItemFragment | null;
  callFromPickerPage?: boolean;
}) {
  const { t } = useTranslate();

  const today = todayIs();
  const pickup = props.branch.openingTimes?.filter(
    (item) => item.dayOfWeek == today && item.service == OpeningTimeServiceType.OnlineOrderPickup
  );

  const delivery = props.branch.openingTimes?.filter(
    (item) => item.dayOfWeek == today && item.service == OpeningTimeServiceType.OnlineOrderDelivery
  );

  if (!props.configuration) {
    return <>Can not get online order branch configuration</>;
  }

  return (
    <>
      <Card className="business-card card">
        {props.branch.company.hubPage?.banner && (
          <div
            style={{
              backgroundImage: `url(${import.meta.env.VITE_CDN_HOST as string}/${props.branch.company.hubPage.banner ?? ''})`,
            }}
            className="business-card__banner"
          ></div>
        )}
        <CardContent>
          <div className="business-card__content">
            <div className="business-card__main">
              <div className="business-card__header">
                <div className="business-card__info">
                  <div className="business-card__identity">
                    {props.branch.company.logo && (
                      <img
                        className="business-card__logo"
                        src={CDNLink(props.branch.company.logo)}
                        alt={props.branch.company.name}
                      />
                    )}
                    <div className="business-card__details">
                      <p className="business-card__name">{props.branch.company.name}</p>
                      <p className="business-card__address">
                        <span>{props.branch.streetAddress},</span>
                        <span>{props.branch.postalCode}</span>
                        <span>
                          {props.branch.addressLocality}
                          {props.branch.addressCountry && <>,</>}{' '}
                        </span>
                        <span>{props.branch.addressCountry}</span>
                      </p>
                    </div>
                  </div>

                  <div className="business-card__actions">
                    {props.branch.contactPhone && (
                      <a
                        href={`tel:${props.branch.contactPhone}`}
                        className="business-card__phone-btn"
                      >
                        <PhoneIcon className="h-4 w-4" />
                        <span>{props.branch.contactPhone}</span>
                      </a>
                    )}
                    {props.branch.contactEmail && (
                      <a
                        href={`mailto:${props.branch.contactEmail}`}
                        className="business-card__email-btn"
                      >
                        <EnvelopeIcon className="h-4 w-4" />
                        <span>{props.branch.contactEmail}</span>
                      </a>
                    )}

                    <div className="business-card__language-selector">
                      <LanguageSelectorAdvanced
                        customerDefaultLanguage={props.branch.company.settings.customerDefaultLanguage}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="business-card__hours">
                {props.configuration.pickupEnable && pickup && pickup.length > 0 && (
                  <div className="business-card__pickup-hours">
                    <span>{t('online-order.business-card.pickup', 'Today pickup')}:</span>
                    {pickup.map((item, index) => (
                      <p key={index}>
                        {item.openTime} - {item.closeTime}
                        {index < pickup.length - 1 && <span>,</span>}
                      </p>
                    ))}
                  </div>
                )}
                {props.configuration.deliveryEnable && delivery && delivery.length > 0 && (
                  <div className="business-card__delivery-hours">
                    <span>{t('online-order.business-card.delivery', 'Today delivery')}:</span>
                    {delivery.map((item, index) => (
                      <p key={index}>
                        {item.openTime} - {item.closeTime}
                        {index < delivery.length - 1 && <span>,</span>}
                      </p>
                    ))}
                  </div>
                )}

                <div className="business-card__mobile-extras">
                  <HappyHourBanner
                    configuration={props.configuration}
                    branchId={props.branch.id}
                    currency={props.branch.company.settings.currency}
                  />

                  {props.callFromPickerPage && <SwitchModeButton branchId={props.branch.id} />}
                </div>
              </div>
            </div>

            <div className="business-card__desktop-extras">
              <HappyHourBanner
                configuration={props.configuration}
                branchId={props.branch.id}
                currency={props.branch.company.settings.currency}
              />

              {props.callFromPickerPage && <SwitchModeButton branchId={props.branch.id} />}
            </div>
          </div>
        </CardContent>
      </Card>
    </>
  );
}
